'use client';

import React, { memo } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Collapse,
} from '@mui/material';
import { KeyboardArrowDownIcon, KeyboardArrowUpIcon } from '@/shared/ui/display/EmojiIcon';
import { DataTableColumn, DataTableRow } from './types';
import { TableCell } from './components/TableCell';
import { TableHeaderCell } from './components/TableHeaderCell';
import { useDataTableTheme } from './theme';

interface IProps {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  expandable?: boolean;
  renderExpandedContent?: (row: DataTableRow) => React.ReactNode;
  onRowClick?: (row: DataTableRow) => void;
  stickyHeader?: boolean;
  maxHeight?: string | number;
}

/**
 * DataTable 컴포넌트
 *
 * 다양한 데이터 타입을 지원하는 테이블 컴포넌트입니다.
 * - string: 일반 문자열
 * - number: 숫자 (포맷팅 옵션 지원)
 * - date: 날짜 (포맷 옵션 지원)
 * - datetime: 날짜시간 (포맷 옵션 지원)
 * - label: 색상이 있는 라벨/배지 형태
 */
function DataTableComponent({
  columns,
  rows,
  expandable = false,
  renderExpandedContent,
  onRowClick,
  stickyHeader = false,
  maxHeight,
}: IProps) {
  const { getContainerSx, getHeaderSx, getRowSx, getCellSx, getExpandableSx } = useDataTableTheme();
  const [expandedRows, setExpandedRows] = React.useState<Set<string | number>>(new Set());

  const handleToggleExpand = (rowId: string | number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const getRowId = (row: DataTableRow, index: number): string | number => {
    return row.id ?? row._id ?? index;
  };

  const tableContent = (
    <MuiTable stickyHeader={stickyHeader}>
      <TableHead>
        <TableRow sx={getHeaderSx()}>
          {expandable && <MuiTableCell sx={{ width: '48px' }} />}
          {columns.map((column) => (
            <TableHeaderCell key={column.id} column={column} />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <MuiTableCell
              colSpan={columns.length + (expandable ? 1 : 0)}
              align="center"
              sx={getCellSx()}
            >
              <Box py={4}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  데이터가 없습니다.
                </Box>
              </Box>
            </MuiTableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => {
            const rowId = getRowId(row, index);
            const isExpanded = expandedRows.has(rowId);

            return (
              <React.Fragment key={rowId}>
                <TableRow
                  hover
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    ...getRowSx(true, false),
                    cursor: onRowClick ? 'pointer' : 'default',
                  }}
                >
                  {expandable && (
                    <MuiTableCell sx={getCellSx()}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleExpand(rowId);
                        }}
                        sx={{
                          padding: '4px',
                        }}
                      >
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </MuiTableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={`${rowId}-${column.id}`}
                      column={column}
                      value={row[column.id]}
                      row={row}
                    />
                  ))}
                </TableRow>
                {expandable && renderExpandedContent && (
                  <TableRow>
                    <MuiTableCell
                      colSpan={columns.length + 1}
                      sx={{
                        padding: 0,
                        borderBottom: isExpanded ? undefined : 'none',
                        ...(isExpanded && getExpandableSx()),
                      }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ padding: 2 }}>{renderExpandedContent(row)}</Box>
                      </Collapse>
                    </MuiTableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })
        )}
      </TableBody>
    </MuiTable>
  );

  if (maxHeight) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          maxHeight,
          overflowX: 'auto',
          overflowY: 'auto',
          boxShadow: 'none', // 쉐도우 제거
          ...getContainerSx(),
        }}
      >
        {tableContent}
      </TableContainer>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none', // 쉐도우 제거
          flex: 1,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'auto', // 수직 스크롤 활성화
          ...getContainerSx(),
        }}
      >
        {tableContent}
      </TableContainer>
    </Box>
  );
}

export const DataTable = memo(DataTableComponent);
